/**
 * The Morning Dispatch (game/newspaper.ts): the nation's own paper, in the house style of a
 * period broadsheet — English headlines in capitals. Numbered variants (`news.quiet.1` …) are
 * picked deterministically; every language must offer the same number of them.
 */
import type { UnitType } from '@friction/engine';
import type { NationRef } from '../names';

const up = (s: string): string => s.toUpperCase();
const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;
const kind = (size: number): string => (size === 3 ? 'metropolis' : size === 2 ? 'city' : 'town');
const TYPE: Record<UnitType, string> = {
  infantry: 'Infantry',
  motorized: 'Motorized',
  armor: 'Armor',
  artillery: 'Artillery',
  recon: 'Reconnaissance',
  hq: 'Headquarters',
  decoy: 'Decoy',
};

export const news = {
  'news.kicker.front': 'FROM THE FRONT',
  'news.kicker.special': 'SPECIAL EDITION',
  'news.kicker.war': 'THE WAR',
  'news.kicker.abroad': 'ABROAD',
  'news.kicker.science': 'SCIENCE & ARMS',
  'news.kicker.curiosities': 'CURIOSITIES',
  'news.kicker.intel': 'INTELLIGENCE',
  'news.kicker.home': 'HOME FRONT',
  'news.kicker.weather': 'WEATHER',

  'news.war.head': 'WAR!',
  'news.war': (p: { theatre: string }) =>
    `Hostilities have broken out across ${p.theatre.replace(/^The /, 'the ')}. The army marches; the nation stands behind it.`,
  'news.capitulates.head': (p: { nation: NationRef | null }) =>
    `${up(p.nation?.name ?? 'the enemy')} CAPITULATES`,
  'news.capitulates.ally': (p: { by: NationRef | null }) =>
    `Our ally has accepted terms from ${p.by?.name ?? 'the enemy'}. We fight on alone on that front.`,
  'news.capitulates.enemy': (p: { byUs: boolean; adj: string | null }) =>
    `Its government has accepted terms from ${p.byUs ? 'our' : `the ${p.adj ?? 'enemy'}`} forces. The guns fall silent there.`,
  'news.cityOurs.1': (p: { city: string }) => `${up(p.city)} IS OURS`,
  'news.cityOurs.2': (p: { city: string }) => `OUR COLUMNS ENTER ${up(p.city)}`,
  'news.cityOurs.3': (p: { city: string }) => `FLAG OVER ${up(p.city)}`,
  'news.cityOurs.capital': (p: { adj: string | null }) =>
    `The ${p.adj ?? 'enemy'} capital has fallen! Bells ring across the nation.`,
  'news.cityOurs.town': (p: { adj: string | null; size: number }) =>
    `The ${p.adj ?? 'enemy'} garrison has been driven from the ${kind(p.size)}. Crowds line the streets.`,
  'news.cityLost.1': (p: { city: string }) => `LINE SHORTENED AT ${up(p.city)}`,
  'news.cityLost.2': (p: { city: string }) => `ORDERLY WITHDRAWAL FROM ${up(p.city)}`,
  'news.cityLost.3': (p: { city: string }) => `${up(p.city)}: TROOPS REDEPLOY`,
  'news.cityLost.capital':
    'The government has moved to safer quarters. The capital, says the Ministry, will be retaken.',
  'news.cityLost.town':
    'The War Ministry speaks of a planned readjustment of the front to stronger positions.',
  'news.cityAbroad.head': (p: { city: string; adj: string | null }) =>
    `${up(p.city)} FALLS TO ${up(p.adj ?? 'enemy')} ARMS`,
  'news.cityAbroad': (p: { size: number; from: NationRef | null; to: NationRef | null }) =>
    `The ${kind(p.size)} changes hands in the fighting between ${p.from?.name ?? 'the enemy'} and ${p.to?.name ?? 'the enemy'}.`,
  'news.hqOurs.head': 'STAFF MOVES TO NEW QUARTERS',
  'news.hqOurs': 'A headquarters has relocated under trying circumstances. Orders may be slow for a time.',
  'news.hqTheirs.head': 'ENEMY STAFF SCATTERED',
  'news.hqTheirs': (p: { adj: string | null }) =>
    `A ${p.adj ?? 'enemy'} headquarters has been overrun. Its maps are said to be in our hands.`,
  'news.tech.head': (p: { tech: string }) => `${up(p.tech)} ENTERS SERVICE`,
  'news.decoy.head': 'ENEMY “ARMY” MADE OF CANVAS',
  'news.decoy': 'Our patrols {at} found a whole division of plywood and paint.',
  'news.stand.head': (p: { at: string }) => `HEROIC STAND ${up(p.at)}`,
  'news.stand': (p: { n: number; pockets: number }) =>
    `${plural(p.n, 'division')} of ours, cut off by the enemy, ${p.n === 1 ? 'holds' : 'hold'} out${p.pockets > 1 ? ` in ${p.pockets} pockets` : ' in the pocket'}. Relief is said to be on its way.`,
  'news.trapped.head': (p: { n: number; adj: string | null; at: string }) =>
    `${p.n} ${up(p.adj ?? 'enemy')} ${p.n === 1 ? 'DIVISION' : 'DIVISIONS'} TRAPPED ${up(p.at)}`,
  'news.trapped': (p: { pockets: number }) =>
    `The ring${p.pockets > 1 ? 's are' : ' is'} closed. Our troops hold the perimeter while hunger does its work.`,
  'news.couriers.head': (p: { n: number }) =>
    p.n === 1 ? 'ENEMY DISPATCH RIDER TAKEN' : `${p.n} ENEMY COURIERS CAPTURED`,
  'news.couriers': 'The orders found in their saddlebags are on their way to the General Staff.',
  'news.withheld.head': 'A STORY WITHHELD',
  'news.withheld':
    'A report on the work of our signals service has been removed at the request of the censor.',
  'news.enemyLost.prisoners': 'THOUSANDS OF PRISONERS TAKEN',
  'news.enemyLost.one': (p: { at: string }) => `ENEMY DIVISION DESTROYED ${up(p.at)}`,
  'news.enemyLost.many': '{n} ENEMY DIVISIONS DESTROYED',
  'news.enemyLost': (p: { n: number; unit: string; surrendered: boolean }) =>
    `${p.n === 1 ? 'The' : 'Among them the'} ${p.unit} ${p.surrendered ? 'has laid down its arms' : 'is no more'}. The enemy's losses are heavy.`,
  'news.ownLost.one': 'A DIVISION GIVES ITS ALL',
  'news.ownLost.many': 'HEAVY FIGHTING; THE NATION MOURNS',
  'news.ownLost': 'The {unit} fought to the last {at}. Its colours will be restored.',
  'news.won.head': (p: { at: string }) => `VICTORIOUS ACTIONS ${up(p.at)}`,
  'news.won': (p: { won: number; lost: number }) =>
    `${plural(p.won, 'engagement')} decided in our favour${p.lost ? `, ${p.lost} still hard-pressed` : ''}. Morale is excellent.`,
  'news.lostBattles.head': (p: { at: string }) => `HARD FIGHTING ${up(p.at)}`,
  'news.lostBattles': (p: { n: number }) =>
    `Our troops meet the enemy with determination. ${plural(p.n, 'position')} changed hands.`,
  'news.flares.head': (p: { at: string }) => `FIGHTING FLARES ${up(p.at)}`,
  'news.flares': (p: { n: number }) =>
    `${plural(p.n, 'action')} reported. Our correspondents are on their way.`,
  'news.raised.one': (p: { type: UnitType }) => `NEW ${up(TYPE[p.type])} DIVISION RAISED`,
  'news.raised.many': '{n} NEW DIVISIONS RAISED',
  'news.raised': (p: { city: string | null }) =>
    `Recruiting offices${p.city ? ` at ${p.city}` : ''} report long queues of volunteers.`,
  'news.clockOurs.head': 'VICTORY WITHIN REACH',
  'news.clockOurs': "Our armies hold the nation's prize cities. Hold them {h} hours more and the war is won.",
  'news.clockTheirs.head': 'GRAVE HOURS FOR THE NATION',
  'news.clockTheirs':
    'The enemy holds the greater part of the prize cities. In {h} hours it may be too late.',

  // ---- quiet days
  'news.quiet.1.head': 'QUIET NIGHT ALONG THE FRONT',
  'news.quiet.1.text': 'Patrols report no change. At headquarters the lamps burned until dawn.',
  'news.quiet.2.head': 'WAR MINISTRY: “ALL IS IN ORDER”',
  'news.quiet.2.text': 'A spokesman declined to comment on rumours of a coming offensive.',
  'news.quiet.3.head': 'RAIN ON THE ROADS',
  'news.quiet.3.text': 'Supply columns labour through the mud; the cavalry is said to grumble.',
  'news.quiet.4.head': 'HARVEST BROUGHT IN BEHIND THE LINES',
  'news.quiet.4.text': 'Farmers work the fields under the eyes of the reserve battalions.',
  'news.quiet.5.head': 'STAFF STUDIES THE MAPS',
  'news.quiet.5.text': 'Officers pore over the situation map. “Every order takes time,” one remarks.',
  'news.filler.1.kicker': 'HOME FRONT',
  'news.filler.1.head': 'BREAD PRICES STEADY',
  'news.filler.1.text': 'Coal and bread unchanged; tobacco remains scarce in the capital.',
  'news.filler.2.kicker': 'NOTICES',
  'news.filler.2.head': 'BLACKOUT AFTER 20:00',
  'news.filler.2.text': 'Citizens are reminded that the blackout regulations remain in force.',
  'news.filler.3.kicker': 'LETTERS',
  'news.filler.3.head': 'FIELD POST FREE OF CHARGE',
  'news.filler.3.text': 'Families may write to the front without stamps. Keep it cheerful.',
  'news.filler.4.kicker': 'CULTURE',
  'news.filler.4.head': 'THE OPERA PLAYS ON',
  'news.filler.4.text': 'The national opera opens its season, war or no war.',
  'news.filler.5.kicker': 'CHURCHES',
  'news.filler.5.head': 'BELLS AT DAWN',
  'news.filler.5.text': 'The bells will ring each morning for the army in the field.',

  // ---- the weather column
  'news.wx.temps': '{min} to {max}',
  'news.wx.frost.head': 'HARD FROST HOLDS',
  'news.wx.frost': 'Rivers frozen fast; the sledges run to the front. {temps}.',
  'news.wx.mud.head': 'MUD ON EVERY ROAD',
  'news.wx.mud': 'The fields have turned to mud; the lorries wait for the frost. {temps}.',
  'news.wx.blizzard.head': 'BLIZZARD WARNING',
  'news.wx.storms.head': 'STORMS APPROACH',
  'news.wx.storm': (p: { dir: string; temps: string }) =>
    `Wind from the ${p.dir}; ${p.temps}. Keep off the exposed roads.`,
  'news.wx.snow.head': 'SNOW EXPECTED',
  'news.wx.rain.head': 'RAIN SPREADING',
  'news.wx.rain': (p: { dir: string; temps: string }) =>
    `Wind from the ${p.dir}; ${p.temps}. The roads will soften.`,
  'news.wx.fair.head': (p: { dir: string }) => `FAIR, WINDS FROM THE ${p.dir}`,
  'news.wx.fair': 'Visibility good for observers; {temps}.',

  // ---- mastheads & mottos
  'news.masthead.1': (p: { adj: string; capital: string }) => `The ${p.capital} Morning Post`,
  'news.masthead.2': (p: { adj: string; capital: string }) => `The ${p.adj} Herald`,
  'news.masthead.3': (p: { adj: string; capital: string }) => `The ${p.adj} Courier`,
  'news.masthead.4': (p: { adj: string; capital: string }) => `The ${p.capital} Gazette`,
  'news.masthead.5': (p: { adj: string; capital: string }) => `The ${p.adj} Tribune`,
  'news.masthead.6': (p: { adj: string; capital: string }) => `The ${p.capital} Chronicle`,
  'news.masthead.7': (p: { adj: string; capital: string }) => `The ${p.adj} Observer`,
  'news.motto.1': 'Passed by the censor',
  'news.motto.2': 'For army and nation',
  'news.motto.3': 'All the news the censor allows',
  'news.motto.4': 'Truth · Duty · Homeland',
  'news.est': 'Est. {year} · {motto}',
} as const;
